import {
  auth as authApi,
  getConfig,
  setToken,
  setCachedUser,
  normalizeGid,
  ApiError,
} from '../api.js';
import { navigate } from '../utils/router.js';
import { esc, toast } from '../utils/dom.js';
import { toggleTheme, getTheme } from '../utils/theme.js';

const TURNSTILE_SITE_KEY = '0x4AAAAAADr4uCxdUJ2Co5YI';

let turnstileScriptPromise = null;
let cachedConfig = null;

async function loadAuthConfig() {
  if (cachedConfig) return cachedConfig;
  try {
    cachedConfig = await getConfig();
  } catch {
    cachedConfig = { skipTurnstile: false, turnstileSiteKey: TURNSTILE_SITE_KEY };
  }
  return cachedConfig;
}

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve();
  if (turnstileScriptPromise) return turnstileScriptPromise;
  turnstileScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Turnstile 加载失败'));
    document.head.appendChild(s);
  });
  return turnstileScriptPromise;
}

function themeIcon() {
  return getTheme() === 'light' ? '🌙' : '☀️';
}

function shell(inner, { title }) {
  return `
    <div class="auth-page">
      <button type="button" class="auth-theme-toggle" id="auth-theme" title="切换主题">${themeIcon()}</button>
      <div class="auth-card glass">
        <button type="button" class="auth-back" id="auth-back">← 返回首页</button>
        <div class="auth-brand">
          <div class="auth-logo">GS</div>
          <h1>${title}</h1>
          <p>GlobalSchoolOrganization</p>
        </div>
        ${inner}
      </div>
    </div>
  `;
}

function bindChrome(root) {
  root.querySelector('#auth-theme')?.addEventListener('click', () => {
    toggleTheme();
    const btn = root.querySelector('#auth-theme');
    if (btn) btn.textContent = themeIcon();
  });
  root.querySelector('#auth-back')?.addEventListener('click', () => navigate('/'));
}

/**
 * Manage Turnstile: hidden until user clicks 「点击完成人机验证」
 * When backend skipTurnstile is true (local .dev.vars), accepts without widget.
 */
function setupTurnstile(root, boxId) {
  let token = '';
  let widgetId = null;
  let skipTurnstile = false;
  const box = root.querySelector(`#${boxId}`);
  const revealBtn = root.querySelector('[data-turnstile-reveal]');

  const getToken = () => (skipTurnstile ? token || 'dev-skip' : token);
  const isSkipped = () => skipTurnstile;

  const reset = () => {
    if (skipTurnstile) {
      token = 'dev-skip';
      return;
    }
    token = '';
    if (widgetId != null && window.turnstile) {
      try {
        window.turnstile.reset(widgetId);
      } catch {
        /* ignore */
      }
    }
  };

  loadAuthConfig().then((cfg) => {
    skipTurnstile = !!cfg.skipTurnstile;
    if (skipTurnstile && revealBtn) {
      token = 'dev-skip';
      revealBtn.textContent = '✓ 本地开发已跳过人机验证';
      revealBtn.disabled = true;
    }
  });

  revealBtn?.addEventListener('click', async () => {
    if (skipTurnstile) {
      token = 'dev-skip';
      revealBtn.textContent = '✓ 本地开发已跳过人机验证';
      revealBtn.disabled = true;
      return;
    }
    box.classList.add('open');
    revealBtn.disabled = true;
    revealBtn.textContent = '人机验证加载中…';
    try {
      await loadTurnstile();
      const cfg = await loadAuthConfig();
      const sitekey = cfg.turnstileSiteKey || TURNSTILE_SITE_KEY;
      box.innerHTML = '';
      widgetId = window.turnstile.render(box, {
        sitekey,
        theme: getTheme() === 'light' ? 'light' : 'dark',
        callback: (t) => {
          token = t;
          revealBtn.textContent = '✓ 人机验证已完成';
        },
        'expired-callback': () => {
          token = '';
          revealBtn.disabled = false;
          revealBtn.textContent = '点击完成人机验证';
        },
        'error-callback': () => {
          token = '';
          toast('人机验证出错，请重试', 'error');
          revealBtn.disabled = false;
          revealBtn.textContent = '点击完成人机验证';
        },
      });
      revealBtn.textContent = '请完成下方验证';
    } catch (e) {
      revealBtn.disabled = false;
      revealBtn.textContent = '点击完成人机验证';
      toast(e.message || '验证组件加载失败', 'error');
    }
  });

  return { getToken, reset, isSkipped };
}

export function renderLogin(root) {
  root.innerHTML = shell(
    `
    <div id="auth-error" class="auth-error hidden"></div>
    <form id="login-form" autocomplete="on">
      <div class="field">
        <label for="login-gid">GID</label>
        <input id="login-gid" name="gid" inputmode="text" autocomplete="username" required placeholder="输入你的 GID" />
      </div>
      <div class="field">
        <label for="login-password">密码</label>
        <input id="login-password" name="password" type="password" autocomplete="current-password" required placeholder="输入密码" />
      </div>
      <div class="auth-actions">
        <button type="button" class="btn btn-ghost btn-block" data-turnstile-reveal>点击完成人机验证</button>
        <div class="turnstile-box" id="login-turnstile"></div>
        <button type="submit" class="btn btn-primary btn-block" id="login-submit">登录账号</button>
      </div>
    </form>
    <div class="auth-footer-link">
      <a href="#/register" id="go-register">没有账号？点击注册</a>
    </div>
    `,
    { title: '登录' }
  );

  bindChrome(root);
  const ts = setupTurnstile(root, 'login-turnstile');
  const errEl = root.querySelector('#auth-error');

  root.querySelector('#go-register')?.addEventListener('click', (e) => {
    e.preventDefault();
    navigate('/register');
  });

  root.querySelector('#login-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    errEl.classList.add('hidden');
    const gidRaw = root.querySelector('#login-gid').value;
    const password = root.querySelector('#login-password').value;
    const gid = normalizeGid(gidRaw);
    if (gid !== gidRaw.trim()) {
      root.querySelector('#login-gid').value = gid;
    }
    const turnstileToken = ts.getToken();
    if (!turnstileToken) {
      errEl.textContent = '请先完成人机验证';
      errEl.classList.remove('hidden');
      return;
    }
    const submit = root.querySelector('#login-submit');
    submit.disabled = true;
    submit.textContent = '登录中…';
    try {
      const res = await authApi.login({ gid, password, turnstileToken });
      const token = res.token || res.accessToken || res.jwt;
      const user = res.user || res.data?.user;
      if (!token) throw new Error('登录响应缺少 token');
      setToken(token);
      if (user) setCachedUser(user);
      toast('登录成功', 'success');
      navigate('/app', { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err.message || '登录失败';
      errEl.textContent = msg;
      errEl.classList.remove('hidden');
      ts.reset();
      const reveal = root.querySelector('[data-turnstile-reveal]');
      if (reveal) {
        reveal.disabled = false;
        reveal.textContent = '点击完成人机验证';
      }
    } finally {
      submit.disabled = false;
      submit.textContent = '登录账号';
    }
  });

  return () => {};
}

export function renderRegister(root) {
  root.innerHTML = shell(
    `
    <div id="auth-error" class="auth-error hidden"></div>
    <div id="register-form-wrap">
      <form id="register-form" autocomplete="on">
        <div class="field">
          <label for="reg-nickname">昵称</label>
          <input id="reg-nickname" name="nickname" required maxlength="32" placeholder="昵称可重复" />
          <span class="hint">昵称不是唯一用户名，允许重复</span>
        </div>
        <div class="field">
          <label for="reg-password">密码</label>
          <input id="reg-password" name="password" type="password" autocomplete="new-password" required minlength="6" placeholder="至少 6 位" />
        </div>
        <div class="field">
          <label for="reg-email">邮箱/手机号 <span class="faint">（选填）</span></label>
          <input id="reg-email" name="email" type="text" autocomplete="username" placeholder="邮箱或手机号，建议填写" />
          <span class="hint">选填，建议填写；忘记密码可凭邮箱/手机号找回。不填写则无法找回密码。</span>
        </div>
        <div class="field">
          <label for="reg-invite">邀请码 <span class="faint">（必填）</span></label>
          <input id="reg-invite" name="invite_code" required maxlength="64" placeholder="请输入邀请码" autocomplete="off" />
          <span class="hint">无邀请码不允许注册。可联系管理员 QQ：3251989534 获取。</span>
        </div>
        <div class="auth-actions">
          <button type="button" class="btn btn-ghost btn-block" data-turnstile-reveal>点击完成人机验证</button>
          <div class="turnstile-box" id="reg-turnstile"></div>
          <button type="submit" class="btn btn-primary btn-block" id="reg-submit">注册账号</button>
        </div>
      </form>
      <div class="auth-footer-link">
        <a href="#/login" id="go-login">已有账号？去登录</a>
      </div>
    </div>
    <div id="register-success" class="auth-result hidden"></div>
    `,
    { title: '注册' }
  );

  bindChrome(root);
  const ts = setupTurnstile(root, 'reg-turnstile');
  const errEl = root.querySelector('#auth-error');

  root.querySelector('#go-login')?.addEventListener('click', (e) => {
    e.preventDefault();
    navigate('/login');
  });

  root.querySelector('#register-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    errEl.classList.add('hidden');
    const nickname = root.querySelector('#reg-nickname').value.trim();
    const password = root.querySelector('#reg-password').value;
    const email = root.querySelector('#reg-email').value.trim();
    const invite_code = root.querySelector('#reg-invite').value.trim();
    const turnstileToken = ts.getToken();
    if (!invite_code) {
      errEl.textContent = '请填写邀请码。无邀请码请联系管理员 QQ：3251989534';
      errEl.classList.remove('hidden');
      return;
    }
    if (!turnstileToken) {
      errEl.textContent = '请先完成人机验证';
      errEl.classList.remove('hidden');
      return;
    }
    const submit = root.querySelector('#reg-submit');
    submit.disabled = true;
    submit.textContent = '注册中…';
    try {
      const res = await authApi.register({
        nickname,
        password,
        email: email || undefined,
        invite_code,
        turnstileToken,
      });
      const token = res.token || res.accessToken || res.jwt;
      const user = res.user || res.data?.user || {};
      const gid = user.gid || user.GID || res.gid;
      if (token) {
        setToken(token);
        if (user.gid) setCachedUser(user);
      }
      root.querySelector('#register-form-wrap').classList.add('hidden');
      const success = root.querySelector('#register-success');
      success.classList.remove('hidden');
      success.innerHTML = `
        <p class="muted">注册成功！你的 GID 为：</p>
        <div class="gid-display">${esc(gid || '—')}</div>
        <p class="text-sm muted">请妥善保存 GID，登录时使用。</p>
        <div class="auth-actions" style="margin-top:1.25rem">
          <button type="button" class="btn btn-primary btn-block" id="reg-go-app">
            ${token ? '进入应用' : '去登录'}
          </button>
        </div>
      `;
      success.querySelector('#reg-go-app')?.addEventListener('click', () => {
        navigate(token ? '/app' : '/login', { replace: true });
      });
      toast('注册成功', 'success');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err.message || '注册失败';
      errEl.textContent = msg;
      errEl.classList.remove('hidden');
      ts.reset();
      const reveal = root.querySelector('[data-turnstile-reveal]');
      if (reveal) {
        reveal.disabled = false;
        reveal.textContent = '点击完成人机验证';
      }
    } finally {
      submit.disabled = false;
      submit.textContent = '注册账号';
    }
  });

  return () => {};
}
